import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { FILE_SYSTEM_CONFIG } from '@lib/constants';
import { CourseCreditResponse } from '@lib/interfaces/course-credits.interface';
import {
    EnrollmentTypeResponse,
    ProgramType,
    ProgramFees,
    ProgramRequest,
    ProgramStep1,
    ProgramStep2,
    ProgramStep3,
} from '@lib/interfaces/program.interface';
import { Observable, shareReplay } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ProgramCreationService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;

    // Cache for user data to prevent multiple API calls
    private usersCache$: Observable<any> | null = null;
    private enrollmentTypesCache$: Observable<EnrollmentTypeResponse> | null = null;

    /**
     * Creates or updates a program
     * @param payload The program data
     * @returns Observable with the response
     */
    createOrUpdateProgram(payload: ProgramRequest): Observable<any> {
        return this.http.patch(`${this.baseUrl}/group-course`, payload);
    }

    /**
     * Completes step 1 of program creation
     * @param data The step 1 data
     * @param courseId Optional course ID for updating an existing program
     * @returns Observable with the response
     */
    completeStepOne(data: ProgramStep1, courseId?: string | null): Observable<any> {
        const payload: ProgramRequest = {
            courseId: courseId || null,
            firstStepper: data,
        };

        return this.createOrUpdateProgram(payload);
    }

    /**
     * Completes step 2 of program creation
     * @param courseId The course ID from step 1
     * @param step1Data The step 1 data
     * @param step2Data The step 2 data
     * @returns Observable with the response
     */
    completeStepTwo(courseId: string, step1Data: ProgramStep1, step2Data: ProgramStep2): Observable<any> {
        const payload: ProgramRequest = {
            courseId,
            firstStepper: step1Data,
            secondStepper: step2Data,
        };

        return this.createOrUpdateProgram(payload);
    }

    /**
     * Completes step 3 of program creation
     * @param courseId The course ID from previous steps
     * @param step1Data The step 1 data
     * @param step2Data The step 2 data
     * @param step3Data The step 3 data
     * @returns Observable with the response
     */
    completeStepThree(
        courseId: string,
        step1Data: ProgramStep1,
        step2Data: ProgramStep2,
        step3Data: ProgramStep3,
    ): Observable<any> {
        const payload: ProgramRequest = {
            courseId,
            firstStepper: step1Data,
            secondStepper: step2Data,
            thirdStepper: step3Data,
        };

        return this.createOrUpdateProgram(payload);
    }

    /**
     * Gets a program by ID
     * @param programId The program ID
     * @returns Observable with the program data
     */
    getProgramById(programId: string): Observable<any> {
        return this.http.get(`${this.baseUrl}/group-course/${programId}`);
    }

    /**
     * Submits a program for approval
     * @param programId The program ID
     * @returns Observable with the response
     */
    submitProgram(programId: string): Observable<any> {
        return this.http.post(`${this.baseUrl}/course/submit/${programId}`, {});
    }

    /**
     * Gets users that can be selected as faculty
     * Uses caching to prevent multiple API calls
     * @returns Observable with the user data
     */
    getUsers(): Observable<any> {
        // If we already have a cached observable, return it
        if (this.usersCache$) {
            return this.usersCache$;
        }

        const params = new HttpParams()
            .append('role', 'FACULTY')
            .append('role', 'HOD')
            .append('role', 'DEAN_FA')
            .append('role', 'DEAN_EO')
            .set('excludeMisc', 'false');
        // Otherwise, create a new observable with caching
        this.usersCache$ = this.http.get(`${this.baseUrl}/user`, { params }).pipe(shareReplay(1));
        return this.usersCache$;
    }

    /**
     * Clears the users cache when needed
     */
    clearUsersCache(): void {
        this.usersCache$ = null;
    }

    /**
     * Gets course credits for a program
     * @param programId The program ID
     * @returns Observable with the course credits data
     */
    getProgramCredits(programId: string): Observable<CourseCreditResponse> {
        return this.http.get<CourseCreditResponse>(`${this.baseUrl}/group-course/credits/${programId}`);
    }

    /**
     * Gets all available enrollment types
     * Uses caching to prevent multiple API calls
     * @returns Observable with the enrollment types
     */
    getEnrollmentTypes(): Observable<EnrollmentTypeResponse> {
        // If we already have a cached observable, return it
        if (this.enrollmentTypesCache$) {
            return this.enrollmentTypesCache$;
        }

        // Otherwise, create a new observable with caching
        this.enrollmentTypesCache$ = this.http
            .get<EnrollmentTypeResponse>(`${this.baseUrl}/enrollment-type`)
            .pipe(shareReplay(1));

        return this.enrollmentTypesCache$;
    }

    /**
     * Clears the enrollment types cache when needed
     */
    clearEnrollmentTypesCache(): void {
        this.enrollmentTypesCache$ = null;
    }

    /**
     * Gets program fees for a course
     * @param courseId The course ID
     * @returns Observable with the program fees data
     */
    getProgramFees(courseId: string): Observable<ProgramFees> {
        return this.http.get<ProgramFees>(`${this.baseUrl}/group-course/get-program-fees/${courseId}`);
    }

    /**
     * Gets topics for a program
     * @param searchParam Optional search parameter
     * @returns Observable with the topics data
     */
    getTopics(searchParam?: string): Observable<any> {
        let params = new HttpParams();
        if (searchParam) {
            params = params.set('searchParam', searchParam);
        }
        return this.http.get(`${this.baseUrl}/topic`, { params });
    }

    uploadProgramCreditFile(file: File, referenceId: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('referenceId', referenceId);
        formData.append('key', FILE_SYSTEM_CONFIG.PROGRAM.KEY);
        formData.append('baseFolderName', FILE_SYSTEM_CONFIG.PROGRAM.BASE_FOLDER_NAME);
        return this.http.post(`${this.baseUrl}/file-system`, formData);
    }

    deleteProgramCreditFile(referenceId: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/file-system/${referenceId}`);
    }

    getProgramTypes(): Observable<ProgramType[]> {
        return this.http.get<ProgramType[]>(`${this.baseUrl}/program?isParent=true`);
    }
}
